<template>
    <div class="site-container">
        <header class="site-header">
            <div class="site-title">{{ $t('siteName') }}</div>
            <language-select></language-select>
            <nav-bar></nav-bar>
        </header>
        <div class="site-body">
            <router-view></router-view>
        </div>
        <site-footer class="site-footer"></site-footer>
    </div>
</template>

<script setup lang="ts">
import LanguageSelect from "@/components/LanguageSelect.vue";
import NavBar from "@/components/nav/NavBar.vue";
import SiteFooter from "@/components/SiteFooter.vue";
</script>

<style>
:root {
    --bg-colour: white;
    --hover-colour: #ccc;
    --active-colour: rgb(40, 40, 40);
    --disabled-colour: lightgrey;
    --enabled-colour: rgb(100, 100, 100);
    --outline-colour: #eee;
    --selected-colour: #ddd;
    --focus-colour: #bbb;
    --ink-colour: black;
    --base-font: "LatoWeb", "lato", arial, helvetica, sans-serif;
    --monospace-font: "Consolas", "Courier New", monospace;
}

@font-face {
    font-family: 'LatoWeb';
    src: url('../assets/fonts/Lato-Regular.woff2') format('woff2'),
         url('../assets/fonts/Lato-Regular.woff') format('woff');
    font-style: normal;
    font-weight: normal;
}

body {
    margin: 0;
    font-family: var(--base-font);
    text-rendering: optimizeLegibility;
    overflow-y: overlay;
}

button {
    font-family: var(--base-font);
}

.site-container {
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    --header-size: 4.75em;
    --footer-size: 5.25em;
    --body-size: calc(100vh - (var(--header-size) + var(--footer-size)));
}

.site-header {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px var(--outline-colour) solid;
    max-height: var(--header-size)
}

.site-title {
    margin: 0.25em;
    user-select: none;
    font-size: 1.75em;
    font-weight: bolder;
}

.site-body {
    display: flex;
    flex-direction: column;
    align-content: center;
    max-height: var(--body-size);
}

.site-footer {
    border-top: 1px var(--outline-colour) solid;
    padding: .25em;
    max-height: var(--footer-size);
    overflow: hidden;
}

.theme-button {
    border: 1px solid var(--ink-colour);
    cursor: pointer;
    min-width: 3em;
    padding-left: 0.25em;
    padding-right: 0.25em;
    display: flex;
    flex-direction: row;
    justify-content: center;
}

.theme-button:hover {
    border-color: var(--enabled-colour);
    color: var(--enabled-colour);
}

.theme-button:active {
    border-color: var(--active-colour);
    stroke: var(--active-colour);
}

.theme-button:disabled {
    border-color: var(--disabled-colour);
    stroke: var(--disabled-colour);
    cursor: unset;
}

.theme-svg-button, .theme-svg-button-disabled {
    fill: var(--bg-colour);
    stroke-width: 12px;
}

.theme-svg-button {
    stroke: var(--enabled-colour);
}

.theme-svg-button:hover {
    stroke: var(--ink-colour);
}

.theme-fill-button {
    border: 1px solid var(--ink-colour);
    cursor: pointer;
}

.theme-fill-button:hover {
    background-color: var(--enabled-colour);
    color: var(--bg-colour);
    border-color: var(--bg-colour);
}

.theme-fill-button:active {
    background-color: var(--active-colour);
    color: var(--bg-colour);
    border-color: var(--bg-colour);
}

.theme-fill-button:disabled {
    background-color: var(--disabled-colour);
    color: var(--bg-colour);
    border-color: var(--bg-colour);
    cursor: unset;
}

.theme-svg-fill-button {
    fill: var(--ink-colour);
    cursor: pointer;
}

.theme-fill-button:hover > .theme-svg-fill-button,
.theme-svg-fill-button:hover {
    background-color: var(--enabled-colour);
    fill: var(--bg-colour);
}

.theme-fill-button:active > .theme-svg-fill-button,
.theme-svg-fill-button:active {
    background-color: var(--active-colour);
    fill: var(--bg-colour);
}

.theme-fill-button:disabled > .theme-svg-fill-button,
.theme-svg-fill-button:disabled, .theme-svg-fill-button-disabled {
    background-color: var(--disabled-colour);
    fill: var(--bg-colour);
    cursor: unset;
}

.theme-fio-button {
    fill: var(--ink-colour);
    cursor: pointer;
}

.theme-fio-button:hover {
    fill: var(--enabled-colour);
}

.theme-fio-button:active {
    fill: var(--active-colour);
}

.theme-fio-button:disabled, .theme-fio-button-disabled {
    fill: var(--disabled-colour);
    cursor: unset;
}

.theme-svg-icon {
    fill: var(--bg-colour);
    stroke: var(--ink-colour);
    stroke-width: 10px;
}

.tiny-icon {
    width: 1em;
    height: 1em;
    margin: 0.1em;
}

.x-small-icon {
    width: 1.5em;
    height: 1.5em;
    margin: 0.1em;
}

.small-icon {
    width: 2em;
    height: 2em;
    margin: 0.1em;
}

.icon-button {
    display: inline-block;
}

/* Firefox specific scrollbar handling */

* {
    scrollbar-width: thin;
}

/* Webkit specific scrollbar handling */
*::-webkit-scrollbar {
    width: .5em;
}

*::-webkit-scrollbar-track {
    background: var(--outline-colour);
}

*::-webkit-scrollbar-thumb {
    background-color: var(--focus-colour);
    border-radius: 2em;
    border: .1em solid var(--outline-colour);
}
</style>
